import React, { useEffect, useRef, useState } from 'react'
import { isNil } from 'lodash'
import classNames from 'classnames'
import { IconCheck, IconMinus } from '@tabler/icons-react'

import './Checkbox.scss'

// Semantic routed `aria-*` and `role` to its inner `<input>` and everything else to the
// wrapper. Keep that split: `aria-label` on a `<label>` element is invalid, while
// `data-testid` has to stay on the root because tests and e2e page objects click the
// root and then drill down to the input.
const INPUT_PROPS = ['autoFocus', 'form', 'required', 'role', 'tabIndex', 'title']

const partitionProps = (props) => {
  const inputProps = {}
  const rootProps = {}

  Object.entries(props).forEach(([prop, propValue]) => {
    if (INPUT_PROPS.includes(prop) || prop.startsWith('aria-')) {
      inputProps[prop] = propValue
    } else {
      rootProps[prop] = propValue
    }
  })

  return { inputProps, rootProps }
}

/**
 * @import import { Checkbox } from '@/components/forms/Checkbox'
 * @param {object} props
 * @param {boolean} [props.checked] Omit for uncontrolled use, where `defaultChecked` seeds the state.
 * @param {boolean} [props.defaultChecked] Initial state for uncontrolled use
 * @param {boolean} [props.indeterminate] Renders the partial-selection dash. Ignored by `Radio`.
 * @param {React.ReactNode} [props.label] Plain inline content only — the root is a `<label>`, so
 *   interactive descendants would suppress native label activation.
 * @param {string} [props.id] Forwarded to the input and echoed in change payloads
 * @param {string} [props.name] Input name grouping radios and identifying form data
 * @param {string} [props.value] Input value echoed in change payloads
 * @param {boolean} [props.disabled] Blocks interaction and dims the control
 * @param {boolean} [props.readOnly] Shows the state but blocks changing it
 * @param {boolean} [props.radio] Renders a radio input; a checked radio can't be unchecked
 * @param {boolean} [props.toggle] Renders as a switch
 * @param {string} [props.className] Extra classes on the root label; `radio-button` restyles a
 *   radio or a checkbox as a full-width card, and `radio-button--described` stacks a
 *   `balto-checkbox-title` over a `balto-checkbox-description` inside that card's label
 * @param {(event: React.SyntheticEvent, data: object) => void} [props.onChange] Called with the
 *   event and `{ id, name, value, checked, indeterminate }`, matching what Semantic passed.
 * @param {(event: React.SyntheticEvent, data: object) => void} [props.onClick] Called on every
 *   click, even when the state can't change
 */
export const Checkbox = ({
  checked,
  defaultChecked = false,
  indeterminate = false,
  label,
  id,
  name,
  value,
  disabled = false,
  readOnly = false,
  radio = false,
  toggle = false,
  className,
  onChange,
  onClick,
  ...props
}) => {
  const inputRef = useRef(null)
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked)

  const isControlled = !isNil(checked)
  const isChecked = isControlled ? Boolean(checked) : uncontrolledChecked

  // No dependency array on purpose: activating a checkbox clears the DOM flag, so a
  // re-render that still wants `indeterminate` has to write it back every time. React
  // has no `indeterminate` attribute, which is why this runs imperatively at all.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate
    }
  })

  // `readonly` is inert on checkbox and radio inputs, and a checked radio can never be
  // unchecked by the user — both have to be enforced here rather than left to the DOM.
  const canToggle = !disabled && !readOnly && !(radio && isChecked)

  const handleChange = (event) => {
    if (!canToggle) {
      return
    }

    if (!isControlled) {
      setUncontrolledChecked(!isChecked)
    }

    if (onChange) {
      onChange(event, { id, name, value, checked: !isChecked, indeterminate: false })
    }
  }

  // Bound to the input, never the root: clicking the label text forwards a second click
  // through the label, so a root-level handler would fire twice per interaction.
  //
  // `onClick` deliberately fires even when the state can't change (Semantic did the same, and
  // callers use it to react to a click on a locked control), so the payload has to be gated —
  // otherwise it would promise a flip that never happens.
  const handleClick = (event) => {
    if (onClick) {
      onClick(event, {
        id,
        name,
        value,
        checked: canToggle ? !isChecked : isChecked,
        indeterminate: canToggle ? false : indeterminate,
      })
    }
  }

  // One interaction must reach an ancestor exactly once. Clicking the label text produces
  // two clicks — the original, then the one the browser forwards to the input — so only
  // the forwarded one is allowed past. Rows like TreeSelect and SearchableDropdownItem
  // toggle selection on click, and a doubled event would cancel itself out.
  const handleRootClick = (event) => {
    if (event.target !== inputRef.current) {
      event.stopPropagation()
    }
  }

  const { inputProps, rootProps } = partitionProps(props)

  // Only the checkbox variant draws a glyph. The radio's dot and the toggle's knob are
  // pseudo-elements on the box, so they need no child here.
  const renderMark = () => {
    if (radio || toggle) {
      return null
    }

    if (indeterminate) {
      return <IconMinus size={14} strokeWidth={3} />
    }

    return isChecked ? <IconCheck size={14} strokeWidth={3} /> : null
  }

  return (
    // The root's onClick is interactive by virtue of the control it wraps, and it only
    // suppresses a duplicate event — it adds no interaction, so it needs no key handler.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <label
      className={classNames(
        'balto-checkbox',
        {
          checked: isChecked,
          indeterminate,
          disabled,
          'read-only': readOnly,
          radio,
          toggle,
        },
        className
      )}
      onClick={handleRootClick}
      {...rootProps}
    >
      <input
        ref={inputRef}
        className="balto-checkbox-input"
        type={radio ? 'radio' : 'checkbox'}
        id={id}
        name={name}
        value={value}
        checked={isChecked}
        disabled={disabled}
        readOnly={readOnly}
        onChange={handleChange}
        onClick={handleClick}
        {...inputProps}
      />
      <span className="balto-checkbox-box" aria-hidden="true">
        {renderMark()}
      </span>
      {!isNil(label) && <span className="balto-checkbox-label">{label}</span>}
    </label>
  )
}

/**
 * @import import { Toggle } from '@/components/forms/Checkbox'
 * @param {object} props Checkbox props, rendered as a switch
 */
export const Toggle = (props) => {
  return <Checkbox {...props} toggle />
}

// `toggle` wins over `radio`, as it did in Semantic. Unlike Semantic the rendered input
// is then `type="checkbox"`: a native checked radio fires no change event, so a
// radio-typed switch could never be turned back off.
/**
 * @import import { Radio } from '@/components/forms/Checkbox'
 * @param {object} props Checkbox props, rendered as a radio
 * @param {boolean} [props.toggle] Renders as a switch instead of a radio
 */
export const Radio = ({ toggle = false, ...props }) => {
  return <Checkbox {...props} radio={!toggle} toggle={toggle} />
}
